import React, { useState, useEffect } from 'react'
import { supabase } from '../utils/supabaseClient'

function CorporateKYBForm({ onClose, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(1) // Step 1-7
  const [loading, setLoading] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(null)
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const [currentUser, setCurrentUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)

  const [formData, setFormData] = useState({
    // CARD 1 - Company Information
  legalName: '',
  tradeName: '',
  entityType: '',
  incorporationDate: '',
  incorporationCountry: '',
  tradeLicenseNumber: '',
  licenseIssuingAuthority: '',
  licenseExpiryDate: '',
  registeredAddress: '',
  operatingAddress: '',
  city: '',
  postalCode: '',
  country: '',
  countryOfOperations: '',
  natureOfBusiness: '',
  businessDescription: '',
  taxId: '',
  businessActivity: '',
  publiclyListed: false,
  contactName: '',
  contactEmail: '',
  contactPhone: '',

  // CARD 2 - Directors & UBOs
  directors: [{
    id: 1,
    fullName: '',
    nationality: '',
    position: '',
    ownershipPercentage: '',
    controlRights: false,
    idType: 'Passport',
    idNumber: '',
    idDocument: null
  }],
  ubos: [{
    id: 1,
    name: '',
    ownershipPercentage: '',
    nationality: '',
    relationship: '',
    idType: 'Passport',
    idNumber: '',
    verificationType: '',
    isAnotherCompany: false,
    idDocument: null
  }],

  // CARD 3 - Source of Funds
  annualTurnover: '',
  mainClients: '',
  numberOfEmployees: '',
  primaryBank: '',
  accountCountry: '',
  sourceOfCapital: '',
  fundingEntityName: '',
  sourceOfFunds: '',

  // CARD 4 - Company Documents
  documents: {
    tradeLicense: null,
    memorandum: null,
    shareCertificate: null,
    taxCertificate: null,
    proofOfAddress: null,
    businessInvoice: null,
    boardResolution: null,
    organizationalChart: null,
    certificateOfIncumbency: null,
    auditReport: null,
    proofOfFunds: null,
    capitalVerification: null,
    invoiceSample: null
  },

  // CARD 5 - Expected Activity
  monthlyTransactionVolume: '',
  avgTransactionValue: '',
  primaryCurrencies: [],
  transactionChannels: [],
  crossBorderPayments: false,
  primaryCorridors: [],
  paymentFrequency: '',
  digitalAssetActivity: false,
  expectedMonthlyTurnover: '',
  expectedCounterparties: '',

  // CARD 6 - Sanctions (Auto-populated)
  sanctionsCheck: {
    pep: false,
    sanctionsHits: [],
    screeningTimestamp: null
  },

  // CARD 7 - Risk Assessment (Read-only, populated by system)
  riskAssessment: {
    automatedRiskScore: 'Pending',
    riskDrivers: [],
    amlAnalystDecision: null,
    mlroDecision: null
  },

  // CARD 8 - Consent & Approval
  purposeOfRelationship: '',
  consentAccepted: false
})


  // Load user session
  useEffect(() => {
    const loadUser = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError || !session) {
          setErrors({ submit: 'Session error. Please login again.' })
          setAuthLoading(false)
          return
        }

        const { data: { user }, error: userError } = await supabase.auth.getUser()
        
        if (userError || !user) {
          setErrors({ submit: 'Unable to get user data. Please login again.' })
        } else {
          setCurrentUser(user)
        }
      } catch (error) {
        setErrors({ submit: 'Authentication error. Please login again.' })
      } finally {
        setAuthLoading(false)
      }
    }

    loadUser()
  }, [])

  // Basic handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleFileChange = (e, docType, section = 'documents', arrayIndex = null) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5242880) {
      setErrors(prev => ({ ...prev, [docType]: 'File size must be less than 5MB' }))
      e.target.value = null
      return
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, [docType]: 'Only JPG, PNG, and PDF files are allowed' }))
      e.target.value = null
      return
    }

    if (section === 'documents') {
      setFormData(prev => ({
        ...prev,
        documents: { ...prev.documents, [docType]: file }
      }))
    } else if (section === 'directors') {
      setFormData(prev => ({
        ...prev,
        directors: prev.directors.map(d => d.id === arrayIndex ? { ...d, idDocument: file } : d)
      }))
    } else if (section === 'ubos') {
      setFormData(prev => ({
        ...prev,
        ubos: prev.ubos.map(u => u.id === arrayIndex ? { ...u, idDocument: file } : u)
      }))
    }

    setErrors(prev => ({ ...prev, [docType]: '' }))
  }

  const removeFile = (docType, section = 'documents', arrayIndex = null) => {
    if (section === 'documents') {
      setFormData(prev => ({
        ...prev,
        documents: { ...prev.documents, [docType]: null }
      }))
    } else if (section === 'directors') {
      setFormData(prev => ({
        ...prev,
        directors: prev.directors.map(d => d.id === arrayIndex ? { ...d, idDocument: null } : d)
      }))
    } else if (section === 'ubos') {
      setFormData(prev => ({
        ...prev,
        ubos: prev.ubos.map(u => u.id === arrayIndex ? { ...u, idDocument: null } : u)
      }))
    }
  }

  // Navigation
  const goToNextStep = () => {
    setCurrentStep(prev => prev + 1)
    window.scrollTo(0, 0)
    setErrors({})
  }

  const goToPreviousStep = () => {
    setCurrentStep(prev => prev - 1)
    window.scrollTo(0, 0)
  }

  // Director handlers
  const handleDirectorChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      directors: prev.directors.map(director =>
        director.id === id ? { ...director, [field]: value } : director
      )
    }))
  }

  const addDirector = () => {
    const newId = Math.max(...formData.directors.map(d => d.id), 0) + 1
    setFormData(prev => ({
      ...prev,
      directors: [...prev.directors, {
        id: newId,
        fullName: '',
        nationality: '',
        position: '',
        ownershipPercentage: '',
        controlRights: false,
        idType: 'Passport',
        idNumber: '',
        idDocument: null
      }]
    }))
  }

  const removeDirector = (id) => {
    if (formData.directors.length > 1) {
      setFormData(prev => ({
        ...prev,
        directors: prev.directors.filter(d => d.id !== id)
      }))
    }
  }

  // UBO handlers
  const handleUBOChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      ubos: prev.ubos.map(ubo =>
        ubo.id === id ? { ...ubo, [field]: value } : ubo
      )
    }))
    if (errors[`ubo_${id}_${field}`]) {
      setErrors(prev => ({ ...prev, [`ubo_${id}_${field}`]: '' }))
    }
  }

  const addUBO = () => {
    const newId = Math.max(...formData.ubos.map(u => u.id), 0) + 1
    setFormData(prev => ({
      ...prev,
      ubos: [...prev.ubos, {
        id: newId,
        name: '',
        ownershipPercentage: '',
        nationality: '',
        relationship: '',
        idType: 'Passport',
        idNumber: '',
        verificationType: '',
        isAnotherCompany: false,
        idDocument: null
      }]
    }))
  }

  const removeUBO = (id) => {
    if (formData.ubos.length > 1) {
      setFormData(prev => ({
        ...prev,
        ubos: prev.ubos.filter(ubo => ubo.id !== id)
      }))
    }
  }

  const getTotalOwnership = () => {
    return formData.ubos.reduce((sum, ubo) => {
      const pct = parseFloat(ubo.ownershipPercentage) || 0
      return sum + pct
    }, 0)
  }

  // Validation
  const validateForm = () => {
    const newErrors = {}

    // ============================================
    // CARD 1 - Company Information
    // ============================================
    if (!formData.legalName.trim()) newErrors.legalName = 'Legal name is required'
    if (!formData.entityType) newErrors.entityType = 'Entity type is required'
    if (!formData.incorporationDate) newErrors.incorporationDate = 'Incorporation date is required'
    if (!formData.incorporationCountry) newErrors.incorporationCountry = 'Country of incorporation is required'
    if (!formData.tradeLicenseNumber.trim()) newErrors.tradeLicenseNumber = 'Trade license is required'
    if (!formData.licenseIssuingAuthority) newErrors.licenseIssuingAuthority = 'Issuing authority is required'
    if (!formData.licenseExpiryDate) newErrors.licenseExpiryDate = 'License expiry date is required'
    if (!formData.registeredAddress.trim()) newErrors.registeredAddress = 'Address is required'
    if (!formData.city.trim()) newErrors.city = 'City is required'
    if (!formData.country) newErrors.country = 'Country is required'
    if (!formData.natureOfBusiness) newErrors.natureOfBusiness = 'Nature of business is required'

    // ============================================
    // CARD 2 - Directors & UBOs
    // ============================================
    formData.directors.forEach((director) => {
      if (!director.fullName.trim()) newErrors[`director_${director.id}_name`] = 'Name required'
      if (!director.nationality) newErrors[`director_${director.id}_nationality`] = 'Nationality required'
      if (!director.position) newErrors[`director_${director.id}_position`] = 'Position required'
    })

    formData.ubos.forEach((ubo) => {
      if (!ubo.name.trim()) newErrors[`ubo_${ubo.id}_name`] = 'UBO name is required'
      if (!ubo.ownershipPercentage) newErrors[`ubo_${ubo.id}_percentage`] = 'Ownership % is required'
      if (!ubo.nationality) newErrors[`ubo_${ubo.id}_nationality`] = 'Nationality required'
    })

    const total = getTotalOwnership()
    if (Math.abs(total - 100) > 0.01) {
      newErrors.uboTotal = `UBO ownership must total 100% (current: ${total.toFixed(2)}%)`
    }

    // ============================================
    // CARD 3 - Source of Funds
    // ============================================
    if (!formData.annualTurnover) newErrors.annualTurnover = 'Annual turnover is required'
    if (!formData.numberOfEmployees) newErrors.numberOfEmployees = 'Number of employees is required'
    if (!formData.mainClients.trim()) newErrors.mainClients = 'Main clients/suppliers are required'
    if (!formData.primaryBank) newErrors.primaryBank = 'Primary bank is required'
    if (!formData.accountCountry) newErrors.accountCountry = 'Bank account country is required'
    if (!formData.sourceOfCapital) newErrors.sourceOfCapital = 'Source of capital is required'
    if (!formData.sourceOfFunds) newErrors.sourceOfFunds = 'Source of funds is required'
    
    // Conditional validation for funding entity
    if (formData.sourceOfCapital === 'Investor' || 
        formData.sourceOfCapital === 'Group' || 
        formData.sourceOfCapital === 'Loan') {
      if (!formData.fundingEntityName.trim()) {
        newErrors.fundingEntityName = 'Funding entity name is required'
      }
    }

    // Proof of funds validation
    if (!formData.documents.proofOfFunds) {
      newErrors.proofOfFunds = 'Proof of funds is required'
    }

    // ============================================
    // CARD 4 - Company Documents
    // ============================================
    if (!formData.documents.tradeLicense) newErrors.tradeLicense = 'Trade license is required'
    if (!formData.documents.memorandum) newErrors.memorandum = 'Memorandum is required'
    if (!formData.documents.proofOfAddress) newErrors.proofOfAddress = 'Proof of address is required'

    // ============================================
    // CARD 5 - Expected Activity
    // ============================================
    if (!formData.monthlyTransactionVolume) newErrors.monthlyTransactionVolume = 'Monthly volume is required'
    if (!formData.avgTransactionValue) newErrors.avgTransactionValue = 'Average transaction value is required'
    if (!formData.paymentFrequency) newErrors.paymentFrequency = 'Payment frequency is required'
    if (!formData.expectedCounterparties.trim()) newErrors.expectedCounterparties = 'Expected counterparties are required'
    
    // Currency validation
    if (formData.primaryCurrencies.length === 0) {
      newErrors.primaryCurrencies = 'Select at least one currency'
    }
    
    // Transaction channels validation
    if (formData.transactionChannels.length === 0) {
      newErrors.transactionChannels = 'Select at least one transaction channel'
    }

    // Cross-border corridors validation
    if (formData.crossBorderPayments && formData.primaryCorridors.length === 0) {
      newErrors.primaryCorridors = 'Select at least one corridor for cross-border payments'
    }

    // ============================================
    // CARD 8 - Consent & Approval
    // ============================================
    if (!formData.purposeOfRelationship) newErrors.purposeOfRelationship = 'Purpose is required'
    if (!formData.consentAccepted) newErrors.consentAccepted = 'You must accept consent'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

    const uploadDocument = async (file, customerId, docType) => {
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${customerId}/${docType}_${Date.now()}.${fileExt}`

      console.log(`📤 Uploading ${docType} to storage...`)
      
      const { data, error } = await supabase.storage
        .from('kyc-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) {
        console.error(`❌ Storage upload error for ${docType}:`, error)
        throw error
      }

      console.log(`✅ Storage upload success for ${docType}, saving metadata...`)

      // Map frontend docType to database-allowed values
      const docTypeMapping = {
        'tradeLicense': 'trade_license',
        'memorandum': 'memorandum_of_association',
        'shareCertificate': 'share_certificate',
        'taxCertificate': 'vat_certificate',
        'proofOfAddress': 'proof_of_address',
        'businessInvoice': 'business_income',
        'boardResolution': 'board_resolution',
        'organizationalChart': 'organizational_chart',
        'certificateOfIncumbency': 'certificate_of_incumbency',
        'auditReport': 'audit_report',
        'proofOfFunds': 'proof_of_funds',           // NEW
        'capitalVerification': 'capital_verification', // NEW
        'invoiceSample': 'invoice_sample'            // NEW
      }

      // For director and UBO documents
      let dbDocumentType = docTypeMapping[docType]
      
      if (!dbDocumentType) {
        // Handle director_X_id and ubo_X_id formats
        if (docType.startsWith('director_')) {
          dbDocumentType = 'director_id'
        } else if (docType.startsWith('ubo_')) {
          dbDocumentType = 'ubo_id'
        } else {
          // Fallback
          dbDocumentType = 'business_income'
        }
      }

      console.log(`📝 Mapped ${docType} to database type: ${dbDocumentType}`)

      // Store the actual document type in file_name for reference
      const { data: insertData, error: dbError } = await supabase
        .from('client_documents')
        .insert({
          client_id: customerId,
          document_type: dbDocumentType,
          file_name: `[${docType}] ${file.name}`,
          file_size: file.size,
          file_type: file.type,
          storage_path: data.path,
          uploaded_by_user_id: currentUser.id
        })
        .select()

      if (dbError) {
        console.error(`❌ Database insert error for ${docType}:`, dbError)
        console.error('Error details:', {
          message: dbError.message,
          details: dbError.details,
          hint: dbError.hint,
          code: dbError.code,
          attemptedDocType: dbDocumentType
        })
        throw dbError
      }

      console.log(`✅ Document metadata saved for ${docType}`)
      return data.path
    } catch (error) {
      console.error(`❌ Error uploading ${docType}:`, error)
      throw error
    }
  }

    // --- tiny parser: returns Number | null | NaN ---
      function parseNumberShort(input) {
        if (input === null || input === undefined) return null;
        let s = String(input).trim();
        if (s === '') return null;

        // remove currency chars and commas, allow ranges with hyphen (-, – , —)
        s = s.replace(/[\$,£€\s]/g, '').replace(/,/g, '');

        // if it's a range like "50k-100k" or "50000-100000"
        const range = s.match(/^([\-]?\d+(\.\d+)?[kKmMbB]?)[\-\u2013\u2014]([\-]?\d+(\.\d+)?[kKmMbB]?)$/);
        const mult = (t) => {
          if (!t) return 1;
          t = t.toLowerCase();
          if (t.endsWith('k')) return 1e3;
          if (t.endsWith('m')) return 1e6;
          if (t.endsWith('b')) return 1e9;
          return 1;
        };

        if (range) {
          const left = parseFloat(range[1].replace(/[kKmMbB]$/i, '')) * mult(range[1]);
          const right = parseFloat(range[3].replace(/[kKmMbB]$/i, '')) * mult(range[3]);
          if (!Number.isFinite(left) || !Number.isFinite(right)) return NaN;
          return (left + right) / 2; // average of range
        }

        // single value with optional suffix like 50k or 1.5M
        const single = s.match(/^([\-]?\d+(\.\d+)?)([kKmMbB])?$/);
        if (single) {
          const base = parseFloat(single[1]);
          const m = single[3] ? mult(single[3]) : 1;
          const val = base * m;
          return Number.isFinite(val) ? val : NaN;
        }

        // fallback: try to extract a number anywhere
        const found = s.match(/[\-]?\d+(\.\d+)?/);
        if (found) return Number(found[0]);

        return NaN;
      }


    const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      console.log('Validation failed')
      return
    }

    if (!currentUser) {
      setErrors({ submit: 'User session not loaded. Please close and reopen the form.' })
      return
    }

    setLoading(true)

    try {
      // Create customer record
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .insert([{
          type: 'Corporate',
          status: 'SUBMITTED',
          created_by: currentUser.id,
          created_at: new Date().toISOString()
        }])
        .select()
        .single()

      if (customerError) throw customerError

      const customerId = customerData.id

      // Upload all documents
      const docUploads = []
      
      Object.entries(formData.documents).forEach(([docType, file]) => {
        if (file && file instanceof File) {
          setUploadingDoc(docType)
          docUploads.push(uploadDocument(file, customerId, docType))
        }
      })

      formData.directors.forEach((d) => {
        if (d.idDocument && d.idDocument instanceof File) {
          setUploadingDoc(`director_${d.id}_id`)
          docUploads.push(uploadDocument(d.idDocument, customerId, `director_${d.id}_id`))
        }
      })

      formData.ubos.forEach((u) => {
        if (u.idDocument && u.idDocument instanceof File) {
          setUploadingDoc(`ubo_${u.id}_id`)
          docUploads.push(uploadDocument(u.idDocument, customerId, `ubo_${u.id}_id`))
        }
      })

      await Promise.all(docUploads)
      setUploadingDoc(null)

      // numeric fields mapping: form key -> DB column name
      const numericMap = {
        annualTurnover: 'annual_turnover',
        expectedMonthlyTurnover: 'expected_monthly_turnover',
        avgTransactionValue: 'avg_transaction_value'
      };

      // convert and validate
      const kybNumeric = {};
      for (const [formKey, dbCol] of Object.entries(numericMap)) {
        const parsed = parseNumberShort(formData[formKey]);
        if (parsed === null) {
          kybNumeric[dbCol] = null; // OK to store NULL in numeric DB column
        } else if (!Number.isFinite(parsed)) {
          setErrors(prev => ({ ...prev, submit: `Please enter a valid number for ${formKey}` }));
          setLoading(false);
          return; // block submit
        } else {
          kybNumeric[dbCol] = parsed;
        }
      }

      // build payload (merge kybNumeric into it)
      const kybPayload = {
        customer_id: customerId,
        legal_name: formData.legalName,
        // ... other non-numeric mappings ...
        ...kybNumeric,
        consent_accepted: formData.consentAccepted,
        consent_timestamp: new Date().toISOString(),
        created_at: new Date().toISOString()
      }


      // Insert KYB Corporate data with ALL new fields
      const { error: kybError } = await supabase
        .from('kyb_corporate')
        .insert([{
          customer_id: customerId,
          
          // CARD 1 fields
          legal_name: formData.legalName,
          trade_name: formData.tradeName,
          entity_type: formData.entityType,
          trade_license_number: formData.tradeLicenseNumber,
          license_issuing_authority: formData.licenseIssuingAuthority,
          license_expiry_date: formData.licenseExpiryDate,
          tax_id: formData.taxId,
          business_activity: formData.businessActivity,
          incorporation_country: formData.incorporationCountry,
          incorporation_date: formData.incorporationDate,
          registered_address: formData.registeredAddress,
          operating_address: formData.operatingAddress,
          city: formData.city,
          postal_code: formData.postalCode,
          country: formData.country,
          country_of_operations: formData.countryOfOperations,
          nature_of_business: formData.natureOfBusiness,
          business_description: formData.businessDescription,
          publicly_listed: formData.publiclyListed,
          contact_name: formData.contactName,
          contact_email: formData.contactEmail,
          contact_phone: formData.contactPhone,
          
          // CARD 3 fields (including NEW fields)
          annual_turnover: formData.annualTurnover,
          main_clients: formData.mainClients,
          number_of_employees: parseInt(formData.numberOfEmployees) || null,
          primary_bank_name: formData.primaryBankName,
          account_country: formData.accountCountry,
          source_of_capital: formData.sourceOfCapital,
          funding_entity_name: formData.fundingEntityName,
          source_of_funds: formData.sourceOfFunds,
          
          // CARD 5 fields (including NEW fields)
          expected_counterparties: formData.expectedCounterparties,
          expected_monthly_turnover: parseFloat(formData.expectedMonthlyTurnover) || null,
          monthly_transaction_volume: formData.monthlyTransactionVolume,
          avg_transaction_value: formData.avgTransactionValue,
          payment_frequency: formData.paymentFrequency,
          primary_currencies: formData.primaryCurrencies,
          transaction_channels: formData.transactionChannels,
          cross_border_payments: formData.crossBorderPayments,
          primary_corridors: formData.primaryCorridors,
          digital_asset_activity: formData.digitalAssetActivity,
          
          // CARD 8 fields
          purpose_of_relationship: formData.purposeOfRelationship,
          consent_accepted: formData.consentAccepted,
          consent_timestamp: new Date().toISOString(),
          consent_version: '1.0',
          created_at: new Date().toISOString()
        }])

      if (kybError) throw kybError

      // Insert directors
      for (const director of formData.directors) {
        await supabase.from('directors').insert([{
          company_id: customerId,
          full_name: director.fullName,
          nationality: director.nationality,
          position: director.position,
          ownership_percentage: parseFloat(director.ownershipPercentage) || 0,
          control_rights: director.controlRights,
          id_type: director.idType,
          id_number: director.idNumber
        }])
      }

      // Insert UBOs
      for (const ubo of formData.ubos) {
        await supabase.from('ubo_declarations').insert([{
          company_id: customerId,
          full_name: ubo.name,
          ownership_percentage: parseFloat(ubo.ownershipPercentage),
          nationality: ubo.nationality,
          id_type: ubo.idType,
          id_number: ubo.idNumber,
          relationship_to_company: ubo.relationship,
          is_another_company: ubo.isAnotherCompany,
          verification_type: ubo.verificationType
        }])
      }

      // Insert initial risk assessment (CARD 6)
      await supabase.from('risk_assessment').insert([{
        customer_id: customerId,
        automated_risk_score: 'Pending',
        risk_drivers: {
          jurisdiction: formData.country,
          country_of_operations: formData.countryOfOperations,
          cross_border: formData.crossBorderPayments,
          digital_assets: formData.digitalAssetActivity,
          sanctions_hits: formData.sanctionsCheck.sanctionsHits?.length || 0,
          pep_status: formData.sanctionsCheck.pep,
          transaction_volume: formData.monthlyTransactionVolume,
          payment_channels: formData.transactionChannels
        },
        created_at: new Date().toISOString()
      }])

      // Insert risk score record (legacy table - keep for compatibility)
      await supabase.from('risk_scores').insert([{
        customer_id: customerId,
        status: 'Pending',
        created_at: new Date().toISOString()
      }])

      // Insert audit trail
      await supabase.from('audit_trail').insert([{
        customer_id: customerId,
        action_type: 'CREATE',
        entity_type: 'KYB_Corporate',
        user_id: currentUser.id,
        user_email: currentUser.email || 'unknown',
        change_summary: `Corporate KYB created: ${formData.legalName}`,
        details: { 
          formData: { 
            ...formData, 
            documents: 'uploaded',
            riskFactors: {
              jurisdiction: formData.country,
              crossBorder: formData.crossBorderPayments,
              digitalAssets: formData.digitalAssetActivity
            }
          } 
        },
        created_at: new Date().toISOString()
      }])

      console.log('✅ KYC data saved')

      setSuccessMessage('✓ Corporate KYB submitted successfully!')
      console.log('Corporate KYB submission successful')

      setTimeout(() => {
        if (onSuccess) {
          onSuccess(customerId, 'corporate')
        }
      }, 1500)

    } catch (error) {
      console.error('Error submitting form:', error)
      setErrors({ submit: error.message || 'Failed to submit form. Please try again.' })
    } finally {
      setLoading(false)
      setUploadingDoc(null)
    }
  }

  // FileUploadField component
  const FileUploadField = ({ label, docType, required, helpText, section = 'documents', arrayIndex = null }) => {
    const getFile = () => {
      if (section === 'documents') return formData.documents[docType]
      if (section === 'directors') return formData.directors.find(d => d.id === arrayIndex)?.idDocument
      if (section === 'ubos') return formData.ubos.find(u => u.id === arrayIndex)?.idDocument
      return null
    }

    const file = getFile()

    return (
      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>
          {label} {required && '*'}
          {helpText && <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block' }}>{helpText}</span>}
        </label>
        
        {!file ? (
          <div>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileChange(e, docType, section, arrayIndex)}
              style={{ width: '100%', padding: '10px', border: errors[docType] ? '2px solid #f44336' : '1px solid #ccc', borderRadius: '5px', fontSize: '14px' }}
            />
            <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Max 5MB • JPG, PNG, PDF
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
            <span style={{ flex: 1, fontSize: '14px' }}>
              ✓ {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </span>
            <button
              type="button"
              onClick={() => removeFile(docType, section, arrayIndex)}
              style={{ backgroundColor: '#f44336', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
            >
              Remove
            </button>
          </div>
        )}
        {errors[docType] && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors[docType]}</p>}
      </div>
    )
  }

  // PART 3: Loading States & Modal Structure

  if (authLoading) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000
      }}>
        <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '40px', textAlign: 'center' }}>
          <h3>Loading user session...</h3>
          <p style={{ color: '#666' }}>Please wait</p>
        </div>
      </div>
    )
  }

  if (!currentUser && errors.submit) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: '10px', padding: '40px',
          textAlign: 'center', maxWidth: '500px'
        }}>
          <h3 style={{ color: '#f44336' }}>Authentication Error</h3>
          <p style={{ color: '#666', margin: '20px 0' }}>{errors.submit}</p>
          <button onClick={onClose} style={{
            backgroundColor: '#1976d2', color: 'white', border: 'none',
            padding: '12px 24px', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold'
          }}>
            Close and Login Again
          </button>
        </div>
      </div>
    )
  }

  // Continue in Part 4 with main render and card components...
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
      justifyContent: 'center', alignItems: 'center', zIndex: 1000,
      overflow: 'auto', padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '10px', width: '100%',
        maxWidth: '800px', maxHeight: '90vh', overflow: 'auto', padding: '30px'
      }}>
        {/* User info strip */}
        <div style={{
          backgroundColor: '#e3f2fd', padding: '10px 15px', borderRadius: '5px',
          marginBottom: '15px', fontSize: '14px'
        }}>
          ✅ Logged in as: <strong>{currentUser?.email || 'Unknown'}</strong>
        </div>

        {/* Progress Indicator */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginBottom: '30px',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute', top: '15px', left: '0', right: '0',
          height: '2px', backgroundColor: '#e0e0e0', zIndex: 0
        }} />
        
        <div style={{
          position: 'absolute', top: '15px', left: '0',
          width: `${((currentStep - 1) / 7) * 100}%`,
          height: '2px', backgroundColor: '#1976d2', zIndex: 1,
          transition: 'width 0.3s ease'
        }} />

        {[
          { num: 1, label: 'Company Info' },
          { num: 2, label: 'Directors/UBOs' },
          { num: 3, label: 'Source of Funds' },
          { num: 4, label: 'Documents' },
          { num: 5, label: 'Activity' },
          { num: 6, label: 'Risk Assessment' },
          { num: 7, label: 'Sanctions' },
          { num: 8, label: 'Consent' }
        ].map((step) => (
          <div key={step.num} style={{ 
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            flex: 1, position: 'relative', zIndex: 2
          }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              backgroundColor: currentStep >= step.num ? '#1976d2' : '#e0e0e0',
              color: currentStep >= step.num ? 'white' : '#999',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 'bold', fontSize: '14px', marginBottom: '8px',
              transition: 'all 0.3s ease'
            }}>
              {currentStep > step.num ? '✓' : step.num}
            </div>
            <span style={{ 
              fontSize: '11px', 
              color: currentStep >= step.num ? '#0d47a1' : '#999',
              textAlign: 'center',
              fontWeight: currentStep === step.num ? 'bold' : 'normal'
            }}>
              {step.label}
            </span>
          </div>
        ))}
      </div>

        <h2 style={{ color: '#0d47a1', marginBottom: '10px' }}>Corporate KYB Form</h2>
        <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
          All fields marked with * are required as per UAE AML regulations
        </p>

        {/* Notifications */}
        {successMessage && (
          <div style={{ padding: '15px', backgroundColor: '#4caf50', color: 'white',
            borderRadius: '5px', marginBottom: '20px', fontWeight: 'bold' }}>
            {successMessage}
          </div>
        )}

        {errors.submit && (
          <div style={{ padding: '15px', backgroundColor: '#f44336', color: 'white',
            borderRadius: '5px', marginBottom: '20px' }}>
            {errors.submit}
          </div>
        )}

        {uploadingDoc && (
          <div style={{ padding: '15px', backgroundColor: '#ff9800', color: 'white',
            borderRadius: '5px', marginBottom: '20px', fontWeight: 'bold' }}>
            📤 Uploading: {uploadingDoc}...
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          
          {/* CARD 1 - COMPANY INFORMATION */}
          {currentStep === 1 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 1 – Corporate Information
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (CBUAE Art. 4, FATF Rec.24, MoE DNFBP Guide)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '5px',
                padding: '15px',
                marginBottom: '25px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <strong>📌 Important:</strong> All fields marked with * are mandatory as per UAE AML regulations.
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {/* Legal Name */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Legal Entity Name <span style={{ color: '#f44336' }}>*</span>
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '4px' }}>
                      As per trade license (CBUAE Art. 4)
                    </span>
                  </label>
                  <input
                    type="text"
                    name="legalName"
                    value={formData.legalName}
                    onChange={handleChange}
                    placeholder="Enter legal entity name"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.legalName ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.legalName && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.legalName}</p>}
                </div>

                {/* Trade Name */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Trade Name (if different)
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '4px' }}>
                      Required if different from registered
                    </span>
                  </label>
                  <input
                    type="text"
                    name="tradeName"
                    value={formData.tradeName}
                    onChange={handleChange}
                    placeholder="Enter trade name"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Entity Type */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Entity Type <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <select
                    name="entityType"
                    value={formData.entityType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.entityType ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select entity type</option>
                    <option value="LLC">Limited Liability Company (LLC)</option>
                    <option value="FZCO">Free Zone Company (FZCO)</option>
                    <option value="Sole_Proprietorship">Sole Proprietorship</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Branch">Branch Office</option>
                    <option value="Holding">Holding Company</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.entityType && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.entityType}</p>}
                </div>

                {/* Incorporation Date */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Date of Incorporation <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="incorporationDate"
                    value={formData.incorporationDate}
                    onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.incorporationDate ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.incorporationDate && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.incorporationDate}</p>}
                </div>

                {/* Country of Incorporation */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Country of Incorporation <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <select
                    name="incorporationCountry"
                    value={formData.incorporationCountry}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.incorporationCountry ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select country</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="SA">Saudi Arabia</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {errors.incorporationCountry && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.incorporationCountry}</p>}
                </div>

                {/* Trade License Number */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Trade License Number <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="tradeLicenseNumber"
                    value={formData.tradeLicenseNumber}
                    onChange={handleChange}
                    placeholder="e.g., 12345678-9"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.tradeLicenseNumber ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.tradeLicenseNumber && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.tradeLicenseNumber}</p>}
                </div>

                {/* License Issuing Authority */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    License Issuing Authority <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <select
                    name="licenseIssuingAuthority"
                    value={formData.licenseIssuingAuthority}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.licenseIssuingAuthority ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select authority</option>
                    <option value="DED">DED</option>
                    <option value="DIFC">DIFC</option>
                    <option value="DMCC">DMCC</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.licenseIssuingAuthority && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.licenseIssuingAuthority}</p>}
                </div>

                {/* License Expiry Date */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    License Expiry Date <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="licenseExpiryDate"
                    value={formData.licenseExpiryDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.licenseExpiryDate ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.licenseExpiryDate && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.licenseExpiryDate}</p>}
                </div>

                {/* Nature of Business */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Nature of Business <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <select
                    name="natureOfBusiness"
                    value={formData.natureOfBusiness}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.natureOfBusiness ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select business type</option>
                    <option value="Trading">Trading</option>
                    <option value="Services">Services</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Real_Estate">Real Estate</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.natureOfBusiness && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.natureOfBusiness}</p>}
                </div>

                {/* Registered Address */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Registered Office Address <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <textarea
                    name="registeredAddress"
                    value={formData.registeredAddress}
                    onChange={handleChange}
                    placeholder="Enter complete address"
                    rows="2"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.registeredAddress ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                  {errors.registeredAddress && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.registeredAddress}</p>}
                </div>

                {/* City */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    City <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.city ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.city && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.city}</p>}
                </div>

                {/* Country */}
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Country <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.country ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select country</option>
                    <option value="AE">United Arab Emirates</option>
                    <option value="SA">Saudi Arabia</option>
                    <option value="OTHER">Other</option>
                  </select>
                  {errors.country && <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>{errors.country}</p>}
                </div>
              </div>
            </div>
          )}

          {/* CARD 2 - DIRECTORS & UBOs */}
          {currentStep === 2 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 2 – Directors & Ultimate Beneficial Owners
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (FATF Rec.24, CBUAE Art. 4)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#fff3e0',
                border: '1px solid #ff9800',
                borderRadius: '5px',
                padding: '15px',
                marginBottom: '25px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <strong>📌 Important:</strong> Provide details for all directors and UBOs with 10%+ ownership. Total UBO ownership must equal 100%.
              </div>

              {/* DIRECTORS SECTION */}
              <div style={{ 
                marginBottom: '35px', 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px',
                border: '2px solid #1976d2'
              }}>
                <h4 style={{ 
                  color: '#0d47a1', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #1976d2',
                  paddingBottom: '8px'
                }}>
                  👔 Directors
                </h4>

                {formData.directors.map((director, index) => (
                  <div key={director.id} style={{ 
                    padding: '15px', 
                    border: '1px solid #90caf9', 
                    borderRadius: '8px', 
                    marginBottom: '15px',
                    backgroundColor: 'white'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <h5 style={{ margin: 0, color: '#0d47a1', fontSize: '14px', fontWeight: 'bold' }}>
                        Director {index + 1}
                      </h5>
                      {formData.directors.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => removeDirector(director.id)} 
                          style={{ 
                            background: '#f44336', 
                            color: '#fff', 
                            border: 'none', 
                            padding: '6px 12px', 
                            borderRadius: '4px', 
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                          Full Name <span style={{ color: '#f44336' }}>*</span>
                        </label>
                        <input 
                          value={director.fullName} 
                          onChange={(e) => handleDirectorChange(director.id, 'fullName', e.target.value)} 
                          placeholder="Enter full name" 
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: errors[`director_${director.id}_name`] ? '2px solid #f44336' : '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }} 
                        />
                        {errors[`director_${director.id}_name`] && (
                          <p style={{ color: '#f44336', fontSize: '11px', marginTop: '4px' }}>
                            {errors[`director_${director.id}_name`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                          Nationality <span style={{ color: '#f44336' }}>*</span>
                        </label>
                        <select 
                          value={director.nationality} 
                          onChange={(e) => handleDirectorChange(director.id, 'nationality', e.target.value)} 
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: errors[`director_${director.id}_nationality`] ? '2px solid #f44336' : '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">Select nationality</option>
                          <option value="AE">UAE</option>
                          <option value="SA">Saudi Arabia</option>
                          <option value="IN">India</option>
                          <option value="OTHER">Other</option>
                        </select>
                        {errors[`director_${director.id}_nationality`] && (
                          <p style={{ color: '#f44336', fontSize: '11px', marginTop: '4px' }}>
                            {errors[`director_${director.id}_nationality`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                          Position <span style={{ color: '#f44336' }}>*</span>
                        </label>
                        <select 
                          value={director.position} 
                          onChange={(e) => handleDirectorChange(director.id, 'position', e.target.value)} 
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: errors[`director_${director.id}_position`] ? '2px solid #f44336' : '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">Select position</option>
                          <option value="Director">Director</option>
                          <option value="CEO">CEO</option>
                          <option value="Managing_Director">Managing Director</option>
                          <option value="Chairman">Chairman</option>
                        </select>
                        {errors[`director_${director.id}_position`] && (
                          <p style={{ color: '#f44336', fontSize: '11px', marginTop: '4px' }}>
                            {errors[`director_${director.id}_position`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                          Ownership %
                        </label>
                        <input 
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={director.ownershipPercentage} 
                          onChange={(e) => handleDirectorChange(director.id, 'ownershipPercentage', e.target.value)} 
                          placeholder="0.00" 
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }} 
                        />
                      </div>
                    </div>

                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                      <FileUploadField 
                        label="Director ID Document" 
                        docType={`director_${director.id}_id`} 
                        required={false}
                        helpText="Upload passport or national ID"
                        section="directors" 
                        arrayIndex={director.id} 
                      />
                    </div>
                  </div>
                ))}

                <button 
                  type="button" 
                  onClick={addDirector} 
                  style={{ 
                    padding: '12px 20px', 
                    background: '#1976d2', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  + Add Another Director
                </button>
              </div>

              {/* UBOs SECTION */}
              <div style={{ 
                marginBottom: '35px', 
                padding: '20px', 
                backgroundColor: '#f3e5f5', 
                borderRadius: '8px',
                border: '2px solid #9c27b0'
              }}>
                <h4 style={{ 
                  color: '#6a1b9a', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #9c27b0',
                  paddingBottom: '8px'
                }}>
                  👥 Ultimate Beneficial Owners (UBOs)
                </h4>

                {formData.ubos.map((ubo, index) => (
                  <div key={ubo.id} style={{ 
                    padding: '15px', 
                    border: '1px solid #ce93d8', 
                    borderRadius: '8px', 
                    marginBottom: '15px',
                    backgroundColor: 'white'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '15px',
                      paddingBottom: '10px',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <h5 style={{ margin: 0, color: '#6a1b9a', fontSize: '14px', fontWeight: 'bold' }}>
                        UBO {index + 1}
                      </h5>
                      {formData.ubos.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => removeUBO(ubo.id)} 
                          style={{ 
                            background: '#f44336', 
                            color: '#fff', 
                            border: 'none', 
                            padding: '6px 12px', 
                            borderRadius: '4px', 
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                          Full Name <span style={{ color: '#f44336' }}>*</span>
                        </label>
                        <input 
                          value={ubo.name} 
                          onChange={(e) => handleUBOChange(ubo.id, 'name', e.target.value)} 
                          placeholder="Enter full name" 
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: errors[`ubo_${ubo.id}_name`] ? '2px solid #f44336' : '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }} 
                        />
                        {errors[`ubo_${ubo.id}_name`] && (
                          <p style={{ color: '#f44336', fontSize: '11px', marginTop: '4px' }}>
                            {errors[`ubo_${ubo.id}_name`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                          Nationality <span style={{ color: '#f44336' }}>*</span>
                        </label>
                        <select 
                          value={ubo.nationality} 
                          onChange={(e) => handleUBOChange(ubo.id, 'nationality', e.target.value)} 
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: errors[`ubo_${ubo.id}_nationality`] ? '2px solid #f44336' : '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">Select nationality</option>
                          <option value="AE">UAE</option>
                          <option value="SA">Saudi Arabia</option>
                          <option value="IN">India</option>
                          <option value="OTHER">Other</option>
                        </select>
                        {errors[`ubo_${ubo.id}_nationality`] && (
                          <p style={{ color: '#f44336', fontSize: '11px', marginTop: '4px' }}>
                            {errors[`ubo_${ubo.id}_nationality`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                          Ownership % <span style={{ color: '#f44336' }}>*</span>
                        </label>
                        <input 
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={ubo.ownershipPercentage} 
                          onChange={(e) => handleUBOChange(ubo.id, 'ownershipPercentage', e.target.value)} 
                          placeholder="0.00" 
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: errors[`ubo_${ubo.id}_percentage`] ? '2px solid #f44336' : '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }} 
                        />
                        {errors[`ubo_${ubo.id}_percentage`] && (
                          <p style={{ color: '#f44336', fontSize: '11px', marginTop: '4px' }}>
                            {errors[`ubo_${ubo.id}_percentage`]}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '13px' }}>
                          Relationship
                        </label>
                        <select 
                          value={ubo.relationship} 
                          onChange={(e) => handleUBOChange(ubo.id, 'relationship', e.target.value)} 
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">Select relationship</option>
                          <option value="Shareholder">Shareholder</option>
                          <option value="Trustee">Trustee</option>
                          <option value="Beneficiary">Beneficiary</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                      <FileUploadField 
                        label="UBO ID Document" 
                        docType={`ubo_${ubo.id}_id`} 
                        required={true}
                        helpText="Upload passport or national ID"
                        section="ubos" 
                        arrayIndex={ubo.id} 
                      />
                    </div>
                  </div>
                ))}

                <button 
                  type="button" 
                  onClick={addUBO} 
                  style={{ 
                    padding: '12px 20px', 
                    background: '#9c27b0', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%',
                    marginBottom: '15px'
                  }}
                >
                  + Add Another UBO
                </button>

                {/* Total Ownership Warning */}
                <div style={{
                  padding: '12px',
                  backgroundColor: getTotalOwnership() === 100 ? '#e8f5e9' : '#fff3cd',
                  borderRadius: '5px',
                  border: `2px solid ${getTotalOwnership() === 100 ? '#4caf50' : '#ffc107'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ color: getTotalOwnership() === 100 ? '#2e7d32' : '#f57f17' }}>
                      Total UBO Ownership:
                    </strong>
                    <span style={{ 
                      fontSize: '18px', 
                      fontWeight: 'bold',
                      color: getTotalOwnership() === 100 ? '#2e7d32' : '#f57f17'
                    }}>
                      {getTotalOwnership().toFixed(2)}%
                    </span>
                  </div>
                  {getTotalOwnership() !== 100 && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#856404' }}>
                      ⚠️ Must equal 100%
                    </p>
                  )}
                </div>
                {errors.uboTotal && (
                  <p style={{ color: '#f44336', fontSize: '13px', marginTop: '10px', fontWeight: 'bold' }}>
                    {errors.uboTotal}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* CARD 3 - SOURCE OF FUNDS */}
          {currentStep === 3 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 3 – Source of Funds / Source of Wealth
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (CBUAE Art.3, Art.16, FATF Rec.10)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#e8f5e9',
                border: '1px solid #4caf50',
                borderRadius: '5px',
                padding: '15px',
                marginBottom: '25px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <strong>💡 Information:</strong> Provide detailed information about the company's source of funds and wealth.
              </div>

              {/* Business Income Section */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#f3e5f5', 
                borderRadius: '8px',
                border: '2px solid #9c27b0',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#6a1b9a', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #9c27b0',
                  paddingBottom: '8px'
                }}>
                  💰 Business Income
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Annual Turnover */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Annual Turnover (AED) <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <input 
                      type="number"
                      name="annualTurnover" 
                      value={formData.annualTurnover} 
                      onChange={handleChange} 
                      placeholder="e.g., 1000000" 
                      min="0"
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: errors.annualTurnover ? '2px solid #f44336' : '1px solid #ddd', 
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }} 
                    />
                    {errors.annualTurnover && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.annualTurnover}
                      </p>
                    )}
                  </div>

                  {/* Number of Employees */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Number of Employees <span style={{ color: '#f44336' }}>*</span>
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '4px' }}>
                        Required for risk assessment
                      </span>
                    </label>
                    <input 
                      type="number"
                      name="numberOfEmployees" 
                      value={formData.numberOfEmployees} 
                      onChange={handleChange} 
                      placeholder="e.g., 50" 
                      min="0"
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: errors.numberOfEmployees ? '2px solid #f44336' : '1px solid #ddd', 
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }} 
                    />
                    {errors.numberOfEmployees && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.numberOfEmployees}
                      </p>
                    )}
                  </div>

                  {/* Main Clients */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Main Clients / Suppliers <span style={{ color: '#f44336' }}>*</span>
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '4px' }}>
                        List top 3-5 clients or suppliers
                      </span>
                    </label>
                    <textarea 
                      name="mainClients" 
                      value={formData.mainClients} 
                      onChange={handleChange}
                      placeholder="e.g., ABC Trading LLC (UAE), XYZ Corporation (UK), 123 Suppliers (India)"
                      rows={3}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: errors.mainClients ? '2px solid #f44336' : '1px solid #ddd', 
                        borderRadius: '5px',
                        fontSize: '14px',
                        resize: 'vertical',
                        backgroundColor: 'white'
                      }} 
                    />
                    {errors.mainClients && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.mainClients}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Banking Information */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px',
                border: '2px solid #1976d2',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#0d47a1', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #1976d2',
                  paddingBottom: '8px'
                }}>
                  🏦 Banking Information
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Primary Bank */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Primary Bank Name <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <input 
                      name="primaryBank" 
                      value={formData.primaryBank} 
                      onChange={handleChange} 
                      placeholder="e.g., Emirates NBD" 
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: errors.primaryBank ? '2px solid #f44336' : '1px solid #ddd', 
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }} 
                    />
                    {errors.primaryBank && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.primaryBank}
                      </p>
                    )}
                  </div>

                  {/* Account Country */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Bank Account Country <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <select 
                      name="accountCountry" 
                      value={formData.accountCountry} 
                      onChange={handleChange} 
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: errors.accountCountry ? '2px solid #f44336' : '1px solid #ddd', 
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select country</option>
                      <option value="AE">United Arab Emirates</option>
                      <option value="SA">Saudi Arabia</option>
                      <option value="US">United States</option>
                      <option value="GB">United Kingdom</option>
                      <option value="OTHER">Other</option>
                    </select>
                    {errors.accountCountry && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.accountCountry}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Capital Source */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#fff3e0', 
                borderRadius: '8px',
                border: '2px solid #ff9800',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#e65100', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #ff9800',
                  paddingBottom: '8px'
                }}>
                  💎 Source of Capital
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Source of Capital */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Source of Capital <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <select 
                      name="sourceOfCapital" 
                      value={formData.sourceOfCapital} 
                      onChange={handleChange} 
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: errors.sourceOfCapital ? '2px solid #f44336' : '1px solid #ddd', 
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select source</option>
                      <option value="Self-funded">Self-funded (Owner's Capital)</option>
                      <option value="Investor">Investor / Venture Capital</option>
                      <option value="Group">Group / Parent Company</option>
                      <option value="Loan">Bank Loan / Financing</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.sourceOfCapital && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.sourceOfCapital}
                      </p>
                    )}
                  </div>

                  {/* Funding Entity Name (conditional) */}
                  {(formData.sourceOfCapital === 'Investor' || 
                    formData.sourceOfCapital === 'Group' || 
                    formData.sourceOfCapital === 'Loan') && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                        Funding Entity Name <span style={{ color: '#f44336' }}>*</span>
                      </label>
                      <input 
                        name="fundingEntityName" 
                        value={formData.fundingEntityName} 
                        onChange={handleChange} 
                        placeholder="Enter name of investor, parent, or bank" 
                        style={{ 
                          width: '100%', 
                          padding: '10px', 
                          border: errors.fundingEntityName ? '2px solid #f44336' : '1px solid #ddd', 
                          borderRadius: '5px',
                          fontSize: '14px',
                          backgroundColor: 'white'
                        }} 
                      />
                      {errors.fundingEntityName && (
                        <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                          {errors.fundingEntityName}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Source of Funds Description */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Source of Funds Description <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <textarea 
                      name="sourceOfFunds" 
                      value={formData.sourceOfFunds} 
                      onChange={handleChange}
                      placeholder="Provide detailed explanation of fund sources"
                      rows={4}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: errors.sourceOfFunds ? '2px solid #f44336' : '1px solid #ddd', 
                        borderRadius: '5px',
                        fontSize: '14px',
                        resize: 'vertical',
                        backgroundColor: 'white'
                      }} 
                    />
                    {errors.sourceOfFunds && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.sourceOfFunds}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Proof of Funds & Capital Verification - NEW SECTION */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e8f5e9', 
                borderRadius: '8px',
                border: '2px solid #4caf50',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#2e7d32', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #4caf50',
                  paddingBottom: '8px'
                }}>
                  📄 Proof of Funds & Capital Verification
                </h4>

                <FileUploadField 
                  label="Proof of Funds" 
                  docType="proofOfFunds" 
                  required={true}
                  helpText="Bank statements, contracts, or funding agreements (last 6 months)"
                />

                <FileUploadField 
                  label="Capital Verification Proof" 
                  docType="capitalVerification" 
                  required={false}
                  helpText="Bank transfer confirmations or investor certificates"
                />
              </div>

              {/* Summary Box */}
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '25px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#0d47a1', fontSize: '14px' }}>
                  📊 Source of Funds Summary
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Annual Turnover:</strong> {formData.annualTurnover ? `AED ${parseFloat(formData.annualTurnover).toLocaleString()}` : 'Not entered'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Employees:</strong> {formData.numberOfEmployees || 'Not entered'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Primary Bank:</strong> {formData.primaryBank || 'Not entered'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Source of Capital:</strong> {formData.sourceOfCapital || 'Not selected'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CARD 4 - COMPANY DOCUMENTS */}
          {currentStep === 4 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 4 – Company Documents
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (CBUAE Art.3, Art.8, FATF Rec.10)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#fff3e0',
                border: '1px solid #ff9800',
                borderRadius: '5px',
                padding: '15px',
                marginBottom: '25px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <strong>📌 Important:</strong> Upload clear, readable documents. All documents must be valid and not expired.
              </div>

              {/* Mandatory Documents */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#ffebee', 
                borderRadius: '8px',
                border: '2px solid #f44336',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#c62828', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #f44336',
                  paddingBottom: '8px'
                }}>
                  📋 Mandatory Documents
                </h4>

                <FileUploadField 
                  label="Trade License" 
                  docType="tradeLicense" 
                  required={true}
                  helpText="OCR validation will be performed"
                />

                <FileUploadField 
                  label="Memorandum / Articles of Association" 
                  docType="memorandum" 
                  required={true}
                  helpText="Ensure latest version"
                />

                <FileUploadField 
                  label="Proof of Office Address" 
                  docType="proofOfAddress" 
                  required={true}
                  helpText="Utility bill or lease agreement"
                />

                <FileUploadField 
                  label="Board Resolution (for onboarding)" 
                  docType="boardResolution" 
                  required={false}
                  helpText="Signed and dated resolution"
                />
              </div>

              {/* Ownership Documents */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#f3e5f5', 
                borderRadius: '8px',
                border: '2px solid #9c27b0',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#6a1b9a', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #9c27b0',
                  paddingBottom: '8px'
                }}>
                  👥 Ownership & Structure Documents
                </h4>

                <FileUploadField 
                  label="Share Certificate / Share Register" 
                  docType="shareCertificate" 
                  required={false}
                  helpText="Match with UBO ownership percentages"
                />

                <FileUploadField 
                  label="Organizational Chart" 
                  docType="organizationalChart" 
                  required={false}
                  helpText="Required for entities with ≥2 UBOs"
                />

                <FileUploadField 
                  label="Certificate of Incumbency" 
                  docType="certificateOfIncumbency" 
                  required={false}
                  helpText="For holding structures"
                />
              </div>

              {/* Financial Documents */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px',
                border: '2px solid #1976d2',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#0d47a1', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #1976d2',
                  paddingBottom: '8px'
                }}>
                  💰 Financial & Tax Documents
                </h4>

                <FileUploadField 
                  label="Tax Registration / VAT Certificate" 
                  docType="taxCertificate" 
                  required={false}
                  helpText="Optional for UAE SMBs"
                />

                <FileUploadField 
                  label="External Audit Report" 
                  docType="auditReport" 
                  required={false}
                  helpText="For risk rating context"
                />
              </div>

              {/* Document Summary */}
              <div style={{
                backgroundColor: '#e8f5e9',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '25px',
                border: '1px solid #4caf50'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#2e7d32', fontSize: '14px', fontWeight: 'bold' }}>
                  📊 Document Upload Summary
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                  <div>
                    <span style={{ color: formData.documents.tradeLicense ? '#4caf50' : '#999' }}>
                      {formData.documents.tradeLicense ? '✓' : '○'} Trade License
                    </span>
                  </div>
                  <div>
                    <span style={{ color: formData.documents.memorandum ? '#4caf50' : '#999' }}>
                      {formData.documents.memorandum ? '✓' : '○'} Memorandum / AOA
                    </span>
                  </div>
                  <div>
                    <span style={{ color: formData.documents.shareCertificate ? '#4caf50' : '#999' }}>
                      {formData.documents.shareCertificate ? '✓' : '○'} Share Certificate
                    </span>
                  </div>
                  <div>
                    <span style={{ color: formData.documents.taxCertificate ? '#4caf50' : '#999' }}>
                      {formData.documents.taxCertificate ? '✓' : '○'} Tax/VAT Certificate
                    </span>
                  </div>
                  <div>
                    <span style={{ color: formData.documents.proofOfAddress ? '#4caf50' : '#999' }}>
                      {formData.documents.proofOfAddress ? '✓' : '○'} Proof of Address
                    </span>
                  </div>
                  <div>
                    <span style={{ color: formData.documents.boardResolution ? '#4caf50' : '#999' }}>
                      {formData.documents.boardResolution ? '✓' : '○'} Board Resolution
                    </span>
                  </div>
                  <div>
                    <span style={{ color: formData.documents.organizationalChart ? '#4caf50' : '#999' }}>
                      {formData.documents.organizationalChart ? '✓' : '○'} Org Chart
                    </span>
                  </div>
                  <div>
                    <span style={{ color: formData.documents.certificateOfIncumbency ? '#4caf50' : '#999' }}>
                      {formData.documents.certificateOfIncumbency ? '✓' : '○'} Certificate of Incumbency
                    </span>
                  </div>
                  <div>
                    <span style={{ color: formData.documents.auditReport ? '#4caf50' : '#999' }}>
                      {formData.documents.auditReport ? '✓' : '○'} Audit Report
                    </span>
                  </div>
                </div>

                <div style={{ 
                  marginTop: '15px', 
                  paddingTop: '12px', 
                  borderTop: '1px solid #c8e6c9',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <strong style={{ color: '#2e7d32' }}>💾 Storage:</strong> All documents are encrypted with AES-256 at rest. Retained for ≥5 years post-termination.
                </div>
              </div>
            </div>
          )}

          {/* CARD 5 - EXPECTED ACTIVITY */}
          {currentStep === 5 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 5 – Expected Activity
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (CBUAE Art.16, FATF Rec.10)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#e8f5e9',
                border: '1px solid #4caf50',
                borderRadius: '5px',
                padding: '15px',
                marginBottom: '25px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <strong>💡 Information:</strong> Help us understand your expected transaction patterns and business relationships.
              </div>

              {/* Transaction Volume & Frequency */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px',
                border: '2px solid #1976d2',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#0d47a1', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #1976d2',
                  paddingBottom: '8px'
                }}>
                  📊 Transaction Volume & Frequency
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Monthly Transaction Volume */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Monthly Transaction Volume <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <select
                      name="monthlyTransactionVolume"
                      value={formData.monthlyTransactionVolume}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: errors.monthlyTransactionVolume ? '2px solid #f44336' : '1px solid #ddd',
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select volume range</option>
                      <option value="0-50000">AED 0 - 50,000</option>
                      <option value="50000-100000">AED 50,000 - 100,000</option>
                      <option value="100000-500000">AED 100,000 - 500,000</option>
                      <option value="500000-1000000">AED 500,000 - 1,000,000</option>
                      <option value="1000000-5000000">AED 1,000,000 - 5,000,000</option>
                      <option value="5000000+">AED 5,000,000+</option>
                    </select>
                    {errors.monthlyTransactionVolume && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.monthlyTransactionVolume}
                      </p>
                    )}
                  </div>

                  {/* Average Transaction Value */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Average Transaction Value <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <select
                      name="avgTransactionValue"
                      value={formData.avgTransactionValue}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: errors.avgTransactionValue ? '2px solid #f44336' : '1px solid #ddd',
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select average value</option>
                      <option value="0-5000">AED 0 - 5,000</option>
                      <option value="5000-10000">AED 5,000 - 10,000</option>
                      <option value="10000-50000">AED 10,000 - 50,000</option>
                      <option value="50000-100000">AED 50,000 - 100,000</option>
                      <option value="100000-500000">AED 100,000 - 500,000</option>
                      <option value="500000+">AED 500,000+</option>
                    </select>
                    {errors.avgTransactionValue && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.avgTransactionValue}
                      </p>
                    )}
                  </div>

                  {/* Payment Frequency */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Expected Payment Frequency <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <select
                      name="paymentFrequency"
                      value={formData.paymentFrequency}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: errors.paymentFrequency ? '2px solid #f44336' : '1px solid #ddd',
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select frequency</option>
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Ad-hoc">Ad-hoc</option>
                    </select>
                    {errors.paymentFrequency && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.paymentFrequency}
                      </p>
                    )}
                  </div>

                  {/* Expected Monthly Turnover */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                      Expected Monthly Turnover (AED)
                    </label>
                    <input
                      type="number"
                      name="expectedMonthlyTurnover"
                      value={formData.expectedMonthlyTurnover}
                      onChange={handleChange}
                      placeholder="e.g., 100000"
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Expected Counterparties - NEW SECTION */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#fff3e0', 
                borderRadius: '8px',
                border: '2px solid #ff9800',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#e65100', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #ff9800',
                  paddingBottom: '8px'
                }}>
                  🤝 Expected Counterparties
                </h4>

                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Expected Counterparties <span style={{ color: '#f44336' }}>*</span>
                    <span style={{ fontSize: '11px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '4px' }}>
                      List main countries/entities you'll transact with (used for country-based risk scoring)
                    </span>
                  </label>
                  <textarea
                    name="expectedCounterparties"
                    value={formData.expectedCounterparties}
                    onChange={handleChange}
                    placeholder="e.g., UAE-based suppliers, European distributors (Germany, France), Asian manufacturers (China, India)"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: errors.expectedCounterparties ? '2px solid #f44336' : '1px solid #ddd',
                      borderRadius: '5px',
                      fontSize: '14px',
                      resize: 'vertical',
                      backgroundColor: 'white'
                    }}
                  />
                  {errors.expectedCounterparties && (
                    <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                      {errors.expectedCounterparties}
                    </p>
                  )}
                </div>
              </div>

              {/* Currencies & Channels */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#f3e5f5', 
                borderRadius: '8px',
                border: '2px solid #9c27b0',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#6a1b9a', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #9c27b0',
                  paddingBottom: '8px'
                }}>
                  💱 Currencies & Transaction Channels
                </h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Primary Currencies */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                      Primary Currencies <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '10px', 
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '5px',
                      border: errors.primaryCurrencies ? '2px solid #f44336' : '1px solid #ddd'
                    }}>
                      {['AED', 'USD', 'EUR', 'GBP', 'SAR'].map(currency => (
                        <label key={currency} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.primaryCurrencies.includes(currency)}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setFormData(prev => ({
                                ...prev,
                                primaryCurrencies: checked 
                                  ? [...prev.primaryCurrencies, currency]
                                  : prev.primaryCurrencies.filter(c => c !== currency)
                              }))
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px' }}>{currency}</span>
                        </label>
                      ))}
                    </div>
                    {errors.primaryCurrencies && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.primaryCurrencies}
                      </p>
                    )}
                  </div>

                  {/* Transaction Channels */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                      Transaction Channels <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '10px', 
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '5px',
                      border: errors.transactionChannels ? '2px solid #f44336' : '1px solid #ddd'
                    }}>
                      {['Bank Transfer', 'Cash', 'Card', 'Cheque', 'Crypto'].map(channel => (
                        <label key={channel} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.transactionChannels.includes(channel)}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setFormData(prev => ({
                                ...prev,
                                transactionChannels: checked 
                                  ? [...prev.transactionChannels, channel]
                                  : prev.transactionChannels.filter(c => c !== channel)
                              }))
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px' }}>{channel}</span>
                        </label>
                      ))}
                    </div>
                    {errors.transactionChannels && (
                      <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px' }}>
                        {errors.transactionChannels}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Cross-Border Payments */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#fff3e0', 
                borderRadius: '8px',
                border: '2px solid #ff9800',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#e65100', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #ff9800',
                  paddingBottom: '8px'
                }}>
                  🌍 Cross-Border Payments
                </h4>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    cursor: 'pointer',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '5px',
                    border: '1px solid #ddd'
                  }}>
                    <input
                      type="checkbox"
                      name="crossBorderPayments"
                      checked={formData.crossBorderPayments}
                      onChange={(e) => setFormData(prev => ({ ...prev, crossBorderPayments: e.target.checked }))}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                      Does the company make cross-border payments?
                    </span>
                  </label>
                </div>

                {formData.crossBorderPayments && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#fff9e6',
                    borderRadius: '5px',
                    border: '1px solid #ffc107'
                  }}>
                    <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>
                      Primary Corridors <span style={{ color: '#f44336' }}>*</span>
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: '10px', 
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '5px',
                      border: '1px solid #ddd'
                    }}>
                      {['GCC', 'Middle East', 'South Asia', 'Europe', 'North America', 'Africa', 'Asia Pacific'].map(corridor => (
                        <label key={corridor} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minWidth: '45%' }}>
                          <input
                            type="checkbox"
                            checked={formData.primaryCorridors.includes(corridor)}
                            onChange={(e) => {
                              const checked = e.target.checked
                              setFormData(prev => ({
                                ...prev,
                                primaryCorridors: checked 
                                  ? [...prev.primaryCorridors, corridor]
                                  : prev.primaryCorridors.filter(c => c !== corridor)
                              }))
                            }}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: '14px' }}>{corridor}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Digital Assets */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#fce4ec', 
                borderRadius: '8px',
                border: '2px solid #e91e63',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#c2185b', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #e91e63',
                  paddingBottom: '8px'
                }}>
                  🪙 Digital Asset Activity
                </h4>

                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px',
                  cursor: 'pointer',
                  padding: '12px',
                  backgroundColor: 'white',
                  borderRadius: '5px',
                  border: '1px solid #ddd'
                }}>
                  <input
                    type="checkbox"
                    name="digitalAssetActivity"
                    checked={formData.digitalAssetActivity}
                    onChange={(e) => setFormData(prev => ({ ...prev, digitalAssetActivity: e.target.checked }))}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    Company engages in digital asset / cryptocurrency activity
                  </span>
                </label>

                {formData.digitalAssetActivity && (
                  <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#fff9e6',
                    borderRadius: '5px',
                    border: '1px solid #ffc107',
                    fontSize: '13px',
                    color: '#856404'
                  }}>
                    <strong>⚠️ Note:</strong> Enhanced Due Diligence (EDD) will be required for digital asset activities.
                  </div>
                )}
              </div>

              {/* Invoice Sample Upload - NEW SECTION */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e8f5e9', 
                borderRadius: '8px',
                border: '2px solid #4caf50',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#2e7d32', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #4caf50',
                  paddingBottom: '8px'
                }}>
                  📄 Sample Invoice / Contract
                </h4>

                <FileUploadField 
                  label="Sample Invoice or Contract" 
                  docType="invoiceSample" 
                  required={false}
                  helpText="Upload a sample invoice or contract for transaction monitoring (used in invoice monitoring linkage)"
                />
              </div>

              {/* Summary */}
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '25px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#0d47a1', fontSize: '14px' }}>
                  📊 Expected Activity Summary
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Monthly Volume:</strong> {formData.monthlyTransactionVolume || 'Not selected'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Avg Transaction:</strong> {formData.avgTransactionValue || 'Not selected'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Payment Frequency:</strong> {formData.paymentFrequency || 'Not selected'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Currencies:</strong> {formData.primaryCurrencies.length > 0 ? formData.primaryCurrencies.join(', ') : 'None selected'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Channels:</strong> {formData.transactionChannels.length > 0 ? formData.transactionChannels.join(', ') : 'None selected'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Cross-Border:</strong> {formData.crossBorderPayments ? 'Yes' : 'No'}
                  </p>
                  <p style={{ margin: '5px 0' }}>
                    <strong>Digital Assets:</strong> {formData.digitalAssetActivity ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CARD 6 - RISK ASSESSMENT */}
          {currentStep === 6 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 6 – Risk Assessment & Compliance Review
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (FATF Rec.1, CBUAE Art.9)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #1976d2',
                borderRadius: '5px',
                padding: '15px',
                marginBottom: '25px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <strong>ℹ️ Information:</strong> This section displays the automated risk assessment. Final approval will be done by our AML Analyst and MLRO after submission.
              </div>

              {/* Automated Risk Score Display */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#f3e5f5', 
                borderRadius: '8px',
                border: '2px solid #9c27b0',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#6a1b9a', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #9c27b0',
                  paddingBottom: '8px'
                }}>
                  🎯 Automated Risk Score
                </h4>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  {/* Risk Score Indicator */}
                  <div style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '2px solid #e0e0e0',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '10px', fontWeight: 'bold' }}>
                      Calculated Risk Level
                    </div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#ff9800',
                      padding: '10px',
                      backgroundColor: '#fff3e0',
                      borderRadius: '5px'
                    }}>
                      PENDING
                    </div>
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
                      Will be calculated on submission
                    </div>
                  </div>

                  {/* Jurisdiction Risk */}
                  <div style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '2px solid #e0e0e0',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '10px', fontWeight: 'bold' }}>
                      Jurisdiction Risk
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: formData.country === 'AE' ? '#4caf50' : '#ff9800',
                      padding: '10px',
                      backgroundColor: formData.country === 'AE' ? '#e8f5e9' : '#fff3e0',
                      borderRadius: '5px'
                    }}>
                      {formData.country === 'AE' ? 'LOW' : formData.country ? 'MEDIUM' : 'N/A'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                      Based on country: {formData.country || 'Not selected'}
                    </div>
                  </div>

                  {/* Transaction Risk */}
                  <div style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '2px solid #e0e0e0',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '10px', fontWeight: 'bold' }}>
                      Transaction Risk
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: formData.digitalAssetActivity ? '#ff9800' : '#4caf50',
                      padding: '10px',
                      backgroundColor: formData.digitalAssetActivity ? '#fff3e0' : '#e8f5e9',
                      borderRadius: '5px'
                    }}>
                      {formData.digitalAssetActivity ? 'ELEVATED' : 'NORMAL'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
                      Digital assets: {formData.digitalAssetActivity ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>

                {/* Risk Factors */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '15px',
                  borderRadius: '5px',
                  fontSize: '13px',
                  border: '1px solid #e0e0e0'
                }}>
                  <strong style={{ color: '#6a1b9a', fontSize: '14px' }}>📊 Risk Factors:</strong>
                  <ul style={{ margin: '10px 0 0 0', paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li><strong>Country of Operations:</strong> {formData.countryOfOperations || 'Not specified'}</li>
                    <li><strong>Cross-Border Activity:</strong> {formData.crossBorderPayments ? 'Yes ⚠️' : 'No ✓'}</li>
                    <li><strong>Digital Assets:</strong> {formData.digitalAssetActivity ? 'Yes ⚠️' : 'No ✓'}</li>
                    <li><strong>Sanctions Hits:</strong> {formData.sanctionsCheck.sanctionsHits?.length || 0}</li>
                    <li><strong>PEP Status:</strong> {formData.sanctionsCheck.pep ? 'Yes ⚠️' : 'No ✓'}</li>
                    <li><strong>Transaction Volume:</strong> {formData.monthlyTransactionVolume || 'Not specified'}</li>
                  </ul>
                </div>
              </div>

              {/* Risk Score Methodology */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px',
                border: '2px solid #1976d2',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#0d47a1', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #1976d2',
                  paddingBottom: '8px'
                }}>
                  📐 Risk Score Methodology
                </h4>

                <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#424242' }}>
                  <p style={{ margin: '0 0 15px 0' }}>
                    Our automated risk assessment evaluates multiple factors in accordance with FATF Recommendations and CBUAE regulations:
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #ddd' }}>
                      <strong style={{ color: '#1976d2' }}>🌍 Geographic Risk</strong>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                        <li>Country of incorporation</li>
                        <li>Country of operations</li>
                        <li>Cross-border corridors</li>
                      </ul>
                    </div>

                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #ddd' }}>
                      <strong style={{ color: '#1976d2' }}>💼 Business Risk</strong>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                        <li>Nature of business</li>
                        <li>Transaction volume</li>
                        <li>Customer base</li>
                      </ul>
                    </div>

                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #ddd' }}>
                      <strong style={{ color: '#1976d2' }}>👥 Ownership Risk</strong>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                        <li>UBO structure</li>
                        <li>PEP exposure</li>
                        <li>Sanctions screening</li>
                      </ul>
                    </div>

                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #ddd' }}>
                      <strong style={{ color: '#1976d2' }}>💸 Transaction Risk</strong>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                        <li>Payment channels</li>
                        <li>Digital asset activity</li>
                        <li>Cash transactions</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Approval Workflow Info */}
              <div style={{
                backgroundColor: '#fff3e0',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #ff9800'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#e65100', fontSize: '15px', fontWeight: 'bold' }}>
                  📋 Post-Submission Approval Workflow
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '2' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ 
                      width: '32px', height: '32px', borderRadius: '50%', 
                      backgroundColor: '#1976d2', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '12px', fontSize: '14px', fontWeight: 'bold',
                      flexShrink: 0
                    }}>1</span>
                    <div style={{ flex: 1 }}>
                      <strong>Automated Risk Scoring</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>Instant - AI-powered risk calculation</div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ 
                      width: '32px', height: '32px', borderRadius: '50%', 
                      backgroundColor: '#1976d2', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '12px', fontSize: '14px', fontWeight: 'bold',
                      flexShrink: 0
                    }}>2</span>
                    <div style={{ flex: 1 }}>
                      <strong>Sanctions & PEP Screening</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>Instant - Against global watchlists</div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ 
                      width: '32px', height: '32px', borderRadius: '50%', 
                      backgroundColor: '#1976d2', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '12px', fontSize: '14px', fontWeight: 'bold',
                      flexShrink: 0
                    }}>3</span>
                    <div style={{ flex: 1 }}>
                      <strong>AML Analyst Review</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>1-2 business days - Manual verification</div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ 
                      width: '32px', height: '32px', borderRadius: '50%', 
                      backgroundColor: '#1976d2', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '12px', fontSize: '14px', fontWeight: 'bold',
                      flexShrink: 0
                    }}>4</span>
                    <div style={{ flex: 1 }}>
                      <strong>MLRO Final Approval</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>24-48 hours - Money Laundering Reporting Officer sign-off</div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ 
                      width: '32px', height: '32px', borderRadius: '50%', 
                      backgroundColor: '#4caf50', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '12px', fontSize: '14px', fontWeight: 'bold',
                      flexShrink: 0
                    }}>✓</span>
                    <div style={{ flex: 1 }}>
                      <strong style={{ color: '#2e7d32' }}>Account Activation</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>Immediate upon approval</div>
                    </div>
                  </div>
                </div>

                <div style={{
                  marginTop: '15px',
                  padding: '12px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '5px',
                  fontSize: '12px',
                  color: '#0d47a1',
                  border: '1px solid #1976d2'
                }}>
                  <strong>📧 Email Notifications:</strong> You will receive email updates at each stage of the approval process.
                </div>
              </div>

              {/* MLRO Information Box */}
              <div style={{
                backgroundColor: '#f3e5f5',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '25px',
                fontSize: '13px',
                lineHeight: '1.6',
                color: '#666',
                border: '1px solid #9c27b0'
              }}>
                <strong style={{ color: '#6a1b9a' }}>ℹ️ About MLRO Approval:</strong>
                <p style={{ margin: '8px 0 0 0' }}>
                  The Money Laundering Reporting Officer (MLRO) is a mandatory compliance role under UAE AML regulations. 
                  The MLRO is responsible for overseeing all AML/CFT activities and has the final authority to approve or decline customer onboarding. 
                  Enhanced Due Diligence (EDD) may be required for high-risk customers or specific business activities.
                </p>
              </div>
            </div>
          )}

          {/* CARD 7 - SANCTIONS / PEP SCREENING */}
          {currentStep === 7 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 7 – Sanctions / PEP Screening
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (FATF Rec.12, CBUAE Art.9)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #1976d2',
                borderRadius: '5px',
                padding: '15px',
                marginBottom: '25px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <strong>ℹ️ Information:</strong> This section shows automated screening results. Screening runs automatically when you submit the form.
              </div>

              {/* Automated Screening */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#f3e5f5', 
                borderRadius: '8px',
                border: '2px solid #9c27b0',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#6a1b9a', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #9c27b0',
                  paddingBottom: '8px'
                }}>
                  🛡️ Automated Sanctions & PEP Screening
                </h4>

                <div style={{
                  backgroundColor: '#fff9e6',
                  padding: '15px',
                  borderRadius: '5px',
                  marginBottom: '20px',
                  fontSize: '13px',
                  color: '#856404',
                  border: '1px solid #ffc107',
                  lineHeight: '1.6'
                }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: 'bold' }}>
                    📋 Automated Screening Against:
                  </p>
                  <ul style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.8' }}>
                    <li>UN Security Council Sanctions Lists</li>
                    <li>UAE Local Terrorist Lists</li>
                    <li>OFAC (US Treasury) Sanctions Lists</li>
                    <li>EU Sanctions Lists</li>
                    <li>UK HM Treasury Sanctions</li>
                    <li>Interpol Wanted Lists</li>
                    <li>Global PEP Databases</li>
                  </ul>
                </div>

                {/* Screening Results Display */}
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0'
                }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr 1fr', 
                    gap: '20px',
                    marginBottom: '20px'
                  }}>
                    {/* PEP Match */}
                    <div style={{
                      padding: '15px',
                      backgroundColor: formData.sanctionsCheck.pep ? '#fff3e0' : '#e8f5e9',
                      borderRadius: '5px',
                      border: `2px solid ${formData.sanctionsCheck.pep ? '#ff9800' : '#4caf50'}`,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '13px', color: '#666', fontWeight: 'bold', marginBottom: '8px' }}>
                        PEP Match
                      </div>
                      <div style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold',
                        color: formData.sanctionsCheck.pep ? '#e65100' : '#2e7d32'
                      }}>
                        {formData.sanctionsCheck.pep ? 'YES' : 'NO'}
                      </div>
                    </div>

                    {/* Sanctions Hits */}
                    <div style={{
                      padding: '15px',
                      backgroundColor: (formData.sanctionsCheck.sanctionsHits?.length || 0) > 0 ? '#ffebee' : '#e8f5e9',
                      borderRadius: '5px',
                      border: `2px solid ${(formData.sanctionsCheck.sanctionsHits?.length || 0) > 0 ? '#f44336' : '#4caf50'}`,
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '13px', color: '#666', fontWeight: 'bold', marginBottom: '8px' }}>
                        Sanctions Hits
                      </div>
                      <div style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold',
                        color: (formData.sanctionsCheck.sanctionsHits?.length || 0) > 0 ? '#c62828' : '#2e7d32'
                      }}>
                        {formData.sanctionsCheck.sanctionsHits?.length || 0}
                      </div>
                    </div>

                    {/* Last Screened */}
                    <div style={{
                      padding: '15px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '5px',
                      border: '2px solid #9e9e9e',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '13px', color: '#666', fontWeight: 'bold', marginBottom: '8px' }}>
                        Last Screened
                      </div>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold',
                        color: '#424242'
                      }}>
                        {formData.sanctionsCheck.screeningTimestamp 
                          ? new Date(formData.sanctionsCheck.screeningTimestamp).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })
                          : 'Not screened'}
                      </div>
                    </div>
                  </div>

                  {/* Screening Details */}
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#fafafa',
                    borderRadius: '5px',
                    border: '1px solid #e0e0e0'
                  }}>
                    <h5 style={{ 
                      margin: '0 0 12px 0', 
                      fontSize: '14px', 
                      fontWeight: 'bold',
                      color: '#424242'
                    }}>
                      📋 Screening Details
                    </h5>
                    
                    {formData.sanctionsCheck.sanctionsHits && formData.sanctionsCheck.sanctionsHits.length > 0 ? (
                      <div>
                        <p style={{ 
                          margin: '0 0 10px 0', 
                          fontSize: '13px', 
                          color: '#c62828',
                          fontWeight: 'bold'
                        }}>
                          ⚠️ Matches Found:
                        </p>
                        <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#666' }}>
                          {formData.sanctionsCheck.sanctionsHits.map((hit, index) => (
                            <li key={index} style={{ marginBottom: '5px' }}>{hit}</li>
                          ))}
                        </ul>
                        <div style={{
                          marginTop: '15px',
                          padding: '12px',
                          backgroundColor: '#fff3cd',
                          borderRadius: '5px',
                          border: '1px solid #ffc107',
                          fontSize: '12px',
                          color: '#856404'
                        }}>
                          <strong>⚠️ Action Required:</strong> These matches will be reviewed by our compliance team. Enhanced Due Diligence (EDD) may be required.
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#e8f5e9',
                        borderRadius: '5px',
                        border: '1px solid #4caf50',
                        fontSize: '13px',
                        color: '#2e7d32'
                      }}>
                        <strong>✓ No Matches Found</strong>
                        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                          No sanctions, PEP, or watchlist matches detected. Screening will be performed upon submission.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Screening Scope */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px',
                border: '2px solid #1976d2',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#0d47a1', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #1976d2',
                  paddingBottom: '8px'
                }}>
                  🔍 Screening Scope
                </h4>

                <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#424242' }}>
                  <p style={{ margin: '0 0 15px 0' }}>
                    The following individuals and entities will be screened:
                  </p>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #ddd' }}>
                      <strong style={{ color: '#1976d2' }}>👔 Directors</strong>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                        {formData.directors.map((director, idx) => (
                          <li key={director.id}>{director.fullName || `Director ${idx + 1}`}</li>
                        ))}
                      </ul>
                    </div>

                    <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #ddd' }}>
                      <strong style={{ color: '#1976d2' }}>👥 UBOs</strong>
                      <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                        {formData.ubos.map((ubo, idx) => (
                          <li key={ubo.id}>{ubo.name || `UBO ${idx + 1}`}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Information Box */}
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '25px',
                fontSize: '13px',
                lineHeight: '1.6',
                color: '#666'
              }}>
                <strong style={{ color: '#0d47a1' }}>ℹ️ About Screening:</strong>
                <p style={{ margin: '8px 0 0 0' }}>
                  Screening is performed in real-time and is a mandatory compliance requirement under UAE AML/CFT regulations. 
                  All company directors, UBOs, and authorized signatories are screened against international sanctions lists and PEP databases. 
                  Any matches will be reviewed by our Money Laundering Reporting Officer (MLRO). The screening process is continuous and will be repeated periodically throughout the business relationship.
                </p>
              </div>
            </div>
          )}

          {/* CARD 8 - RISK & APPROVAL */}
          {currentStep === 8 && (
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ 
                color: '#0d47a1', fontSize: '18px', marginBottom: '15px',
                borderBottom: '2px solid #0d47a1', paddingBottom: '5px'
              }}>
                CARD 8 – Purpose of Relationship & Final Consent
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                  (CBUAE Art.4, Art.9, FATF Rec.10)
                </span>
              </h3>

              <div style={{
                backgroundColor: '#e8f5e9',
                border: '1px solid #4caf50',
                borderRadius: '5px',
                padding: '15px',
                marginBottom: '25px',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                <strong>💡 Final Step:</strong> Confirm the purpose of your business relationship and accept our data privacy consent to submit your application.
              </div>

              {/* Purpose of Relationship */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '8px',
                border: '2px solid #1976d2',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#0d47a1', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #1976d2',
                  paddingBottom: '8px'
                }}>
                  🎯 Purpose of Business Relationship
                </h4>

                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                    Purpose of Relationship <span style={{ color: '#f44336' }}>*</span>
                  </label>
                  <select 
                    name="purposeOfRelationship" 
                    value={formData.purposeOfRelationship} 
                    onChange={handleChange} 
                    style={{ 
                      width: '100%', 
                      padding: '12px', 
                      border: errors.purposeOfRelationship ? '2px solid #f44336' : '1px solid #ddd', 
                      borderRadius: '5px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      fontWeight: '500'
                    }}
                  >
                    <option value="">-- Select Purpose --</option>
                    <option value="Merchant Payments">Merchant Payment Processing</option>
                    <option value="Payroll">Payroll & Employee Payments</option>
                    <option value="Investment">Investment Management</option>
                    <option value="Corporate Banking">Corporate Banking Services</option>
                    <option value="Trade Finance">Trade Finance & Letters of Credit</option>
                    <option value="Treasury">Treasury & Cash Management</option>
                    <option value="Real Estate">Real Estate Transactions</option>
                    <option value="Business Operations">General Business Operations</option>
                    <option value="Other">Other Services</option>
                  </select>
                  {errors.purposeOfRelationship && (
                    <p style={{ color: '#f44336', fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>
                      {errors.purposeOfRelationship}
                    </p>
                  )}
                </div>
              </div>

              {/* Data Privacy Consent */}
              <div style={{ 
                padding: '20px', 
                backgroundColor: '#f3e5f5', 
                borderRadius: '8px',
                border: '2px solid #9c27b0',
                marginBottom: '25px'
              }}>
                <h4 style={{ 
                  color: '#6a1b9a', 
                  fontSize: '16px', 
                  marginBottom: '15px',
                  borderBottom: '2px solid #9c27b0',
                  paddingBottom: '8px'
                }}>
                  🔒 Data Privacy & Consent
                </h4>

                <div style={{
                  backgroundColor: 'white',
                  padding: '15px',
                  borderRadius: '5px',
                  marginBottom: '20px',
                  fontSize: '13px',
                  color: '#666',
                  lineHeight: '1.7',
                  border: '1px solid #ddd'
                }}>
                  <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#6a1b9a' }}>
                    By submitting this form, the company consents to the following:
                  </p>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px', color: '#424242' }}>
                      1. Data Collection & Processing
                    </p>
                    <p style={{ margin: '0', fontSize: '12px', paddingLeft: '15px' }}>
                      We will collect, process, and store the company's data including corporate documents, 
                      director/UBO information, financial data, and transaction details for KYC/AML compliance.
                    </p>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px', color: '#424242' }}>
                      2. Sanctions & PEP Screening
                    </p>
                    <p style={{ margin: '0', fontSize: '12px', paddingLeft: '15px' }}>
                      Company data, directors, and UBOs will be screened against international sanctions lists, 
                      PEP databases, and watchlists.
                    </p>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px', color: '#424242' }}>
                      3. Data Sharing & Disclosure
                    </p>
                    <p style={{ margin: '0', fontSize: '12px', paddingLeft: '15px' }}>
                      We may share information with regulatory authorities, law enforcement agencies, 
                      and service providers as required by UAE AML/CFT laws.
                    </p>
                  </div>

                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px', color: '#424242' }}>
                      4. Data Retention
                    </p>
                    <p style={{ margin: '0', fontSize: '12px', paddingLeft: '15px' }}>
                      Data will be retained for a minimum of 5 years after the relationship ends, as required by CBUAE regulations.
                    </p>
                  </div>

                  <div style={{ marginBottom: '0' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '13px', color: '#424242' }}>
                      5. Your Rights
                    </p>
                    <p style={{ margin: '0', fontSize: '12px', paddingLeft: '15px' }}>
                      The company has the right to access, correct, or request deletion of its data, 
                      subject to legal and regulatory obligations.
                    </p>
                  </div>
                </div>

                {/* Consent Checkbox */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'start', 
                    padding: '15px',
                    backgroundColor: 'white', 
                    borderRadius: '5px', 
                    cursor: 'pointer',
                    border: errors.consentAccepted ? '2px solid #f44336' : (formData.consentAccepted ? '2px solid #4caf50' : '2px solid #9c27b0')
                  }}>
                    <input 
                      type="checkbox" 
                      name="consentAccepted" 
                      checked={formData.consentAccepted} 
                      onChange={handleChange}
                      style={{ width: '20px', height: '20px', marginRight: '12px', marginTop: '2px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '14px', flex: 1 }}>
                      <strong>I have read and accept the above terms on behalf of the company <span style={{ color: '#f44336' }}>*</span></strong>
                      <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', display: 'block', marginTop: '8px' }}>
                        I confirm that I am authorized to provide this consent on behalf of the company.
                      </span>
                    </span>
                  </label>
                  {errors.consentAccepted && (
                    <p style={{ color: '#f44336', fontSize: '12px', marginTop: '10px', marginLeft: '15px', fontWeight: 'bold' }}>
                      {errors.consentAccepted}
                    </p>
                  )}
                </div>

                {formData.consentAccepted && (
                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: '5px',
                    fontSize: '12px',
                    color: '#2e7d32',
                    border: '1px solid #4caf50'
                  }}>
                    ✓ Consent accepted on {new Date().toLocaleString('en-GB', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })} • Consent Version: 1.0
                  </div>
                )}
              </div>

              {/* Submission Checklist */}
              <div style={{
                backgroundColor: '#fff3e0',
                padding: '20px',
                borderRadius: '8px',
                border: '2px solid #ff9800',
                marginBottom: '25px'
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#e65100', fontSize: '15px', fontWeight: 'bold' }}>
                  📋 Submission Readiness Checklist
                </h4>
                <div style={{ fontSize: '13px', lineHeight: '2' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', height: '24px', borderRadius: '50%', 
                      backgroundColor: formData.legalName ? '#4caf50' : '#e0e0e0',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
                    }}>
                      {formData.legalName ? '✓' : '1'}
                    </span>
                    <span style={{ color: formData.legalName ? '#2e7d32' : '#666' }}>
                      Company Information Completed
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', height: '24px', borderRadius: '50%', 
                      backgroundColor: (formData.directors.length > 0 && formData.ubos.length > 0) ? '#4caf50' : '#e0e0e0',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
                    }}>
                      {(formData.directors.length > 0 && formData.ubos.length > 0) ? '✓' : '2'}
                    </span>
                    <span style={{ color: (formData.directors.length > 0 && formData.ubos.length > 0) ? '#2e7d32' : '#666' }}>
                      Directors & UBOs Declared
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', height: '24px', borderRadius: '50%', 
                      backgroundColor: formData.sourceOfFunds ? '#4caf50' : '#e0e0e0',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
                    }}>
                      {formData.sourceOfFunds ? '✓' : '3'}
                    </span>
                    <span style={{ color: formData.sourceOfFunds ? '#2e7d32' : '#666' }}>
                      Source of Funds Documented
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', height: '24px', borderRadius: '50%', 
                      backgroundColor: formData.documents.tradeLicense ? '#4caf50' : '#e0e0e0',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
                    }}>
                      {formData.documents.tradeLicense ? '✓' : '4'}
                    </span>
                    <span style={{ color: formData.documents.tradeLicense ? '#2e7d32' : '#666' }}>
                      Company Documents Uploaded
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', height: '24px', borderRadius: '50%', 
                      backgroundColor: formData.monthlyTransactionVolume ? '#4caf50' : '#e0e0e0',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
                    }}>
                      {formData.monthlyTransactionVolume ? '✓' : '5'}
                    </span>
                    <span style={{ color: formData.monthlyTransactionVolume ? '#2e7d32' : '#666' }}>
                      Expected Activity Defined
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', height: '24px', borderRadius: '50%', 
                      backgroundColor: '#4caf50',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
                    }}>
                      ✓
                    </span>
                    <span style={{ color: '#2e7d32' }}>
                      Risk Assessment Reviewed
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ 
                      width: '24px', height: '24px', borderRadius: '50%', 
                      backgroundColor: '#4caf50',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
                    }}>
                      ✓
                    </span>
                    <span style={{ color: '#2e7d32' }}>
                      Sanctions Screening Reviewed
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ 
                      width: '24px', height: '24px', borderRadius: '50%', 
                      backgroundColor: formData.consentAccepted ? '#4caf50' : '#e0e0e0',
                      color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', fontSize: '14px', fontWeight: 'bold', flexShrink: 0
                    }}>
                      {formData.consentAccepted ? '✓' : '8'}
                    </span>
                    <span style={{ color: formData.consentAccepted ? '#2e7d32' : '#666' }}>
                      Consent Accepted
                    </span>
                  </div>
                </div>

                {(formData.consentAccepted && formData.purposeOfRelationship && formData.legalName) && (
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '15px', 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: '5px',
                    border: '2px solid #4caf50'
                  }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#2e7d32', fontWeight: 'bold' }}>
                      ✓ Ready for Submission!
                    </p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                      Click "Submit KYB" below to complete your application. Our compliance team will review within 2-3 business days.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          

          {/* Navigation Buttons */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
            {currentStep > 1 && (
              <button 
                type="button" 
                onClick={goToPreviousStep}
                disabled={loading}
                style={{
                  flex: 1, 
                  backgroundColor: '#757575', 
                  color: 'white',
                  border: 'none', 
                  padding: '14px', 
                  borderRadius: '5px',
                  cursor: loading ? 'not-allowed' : 'pointer', 
                  fontWeight: 'bold', 
                  fontSize: '16px'
                }}
              >
                ← Previous
              </button>
            )}
            
            {currentStep < 8 ? (
              <button 
                type="button" 
                onClick={goToNextStep}
                disabled={loading}
                style={{
                  flex: 1, 
                  backgroundColor: '#1976d2', 
                  color: 'white',
                  border: 'none', 
                  padding: '14px', 
                  borderRadius: '5px',
                  cursor: loading ? 'not-allowed' : 'pointer', 
                  fontWeight: 'bold', 
                  fontSize: '16px'
                }}
              >
                Next →
              </button>
            ) : (
              <button 
                type="submit" 
                disabled={loading}
                style={{
                  flex: 1, 
                  backgroundColor: loading ? '#ccc' : '#4caf50', 
                  color: 'white',
                  border: 'none', 
                  padding: '14px', 
                  borderRadius: '5px',
                  cursor: loading ? 'not-allowed' : 'pointer', 
                  fontWeight: 'bold', 
                  fontSize: '16px'
                }}
              >
                {loading ? 'Submitting...' : '✓ Submit for Review'}
              </button>
            )}
            
            <button 
              type="button" 
              onClick={onClose} 
              disabled={loading}
              style={{
                flex: 1, 
                backgroundColor: '#ccc', 
                color: '#333', 
                border: 'none',
                padding: '14px', 
                borderRadius: '5px',
                cursor: loading ? 'not-allowed' : 'pointer', 
                fontWeight: 'bold', 
                fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CorporateKYBForm